import { AnomalyDirection, RunStatus, TestRun } from '../../types';
import { ApiTestRunData } from './types';

const toAnomalyDirection = (
  value: string | null | undefined,
): AnomalyDirection | undefined => {
  switch (value?.toUpperCase()) {
    case AnomalyDirection.Spike:
      return AnomalyDirection.Spike;
    case AnomalyDirection.Drop:
      return AnomalyDirection.Drop;
    case AnomalyDirection.Both:
      return AnomalyDirection.Both;
    default:
      return undefined;
  }
};

export const parseApiTestRun = (item: ApiTestRunData): TestRun => {
  const { test, execution, performanceStats } = item;
  const tableAsset = test.tableAsset;
  const config = test.config;

  const sensitivity =
    config?.anomalyConfig?.sensitivity ?? config?.sensitivity ?? undefined;
  const anomalyDirection = toAnomalyDirection(
    config?.anomalyConfig?.anomalyDirection ?? config?.anomalyDirection,
  );

  return {
    metadata: {
      test_unique_id: execution.testId,
      elementary_unique_id: execution.subTestUniqueId,
      database_name: tableAsset?.dbName || '',
      schema_name: tableAsset?.schemaName || '',
      table_name: tableAsset?.tableName,
      column_name: execution.columnName || undefined,
      test_name: test.name,
      test_display_name: test.name,
      test_tags: test.tags,
      test_owners: test.owners,
      latest_run_time: execution.executionTimestamp,
      latest_run_time_utc: execution.executionTimestamp,
      latest_run_status: execution.status as RunStatus,
      model_unique_id: tableAsset?.id,
      table_unique_id: tableAsset?.id || '',
      test_type: test.type,
      test_sub_type: execution.subType,
      // The remaining test configuration (test_params, timeframe, anomaly
      // threshold, etc.) lives on the polymorphic GraphQL `Test.config`
      // interface and is mapped on demand; only the fields the expanded row
      // renders today are populated here.
      test_params: {},
      configuration: {
        test_name: test.name,
        ...(sensitivity !== undefined ? { sensitivity } : {}),
        ...(anomalyDirection !== undefined
          ? { anomaly_direction: anomalyDirection }
          : {}),
      },
      test_query: execution.query || undefined,
      severity: test.severity || undefined,
      description: test.description || undefined,
    },
    test_runs: {
      fail_rate: performanceStats?.failRate ?? 0,
      totals: {
        errors: performanceStats?.errorsCount ?? 0,
        failures: performanceStats?.failuresCount ?? 0,
        warnings: performanceStats?.warningsCount ?? 0,
        passed: performanceStats?.passedCount ?? 0,
        noData: 0,
      },
      // Per-run invocation history isn't returned by the table query; the
      // expanded-row chart loads it on demand (SaaS: GraphQL
      // `Test.executionHistory`), so it's intentionally empty here.
      invocations: [],
      description: test.description || '',
    },
    last_exec_time: execution.durationSeconds ?? 0,
    median_exec_time: performanceStats?.medianExecTime ?? 0,
    asset_metadata: {
      tags: tableAsset?.tags,
      owners: tableAsset?.owners,
      is_critical: tableAsset?.isCritical,
      subscribers: tableAsset?.subscribers,
      path: tableAsset?.path,
      name: tableAsset?.name,
    },
  };
};
